import { FC, useMemo, useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Product } from '../../../../types/productType';
import { AppContext } from '../../../../context/ContextProvider';

interface TradeDistributionProps {
  soldHoldings: Product[];
  isLoading?: boolean;
}

interface DistributionBucket {
  range: string;
  count: number;
  isProfit: boolean;
}

const TradeDistribution: FC<TradeDistributionProps> = ({ soldHoldings, isLoading }) => {
  const {
    state: {
      settings: { isDarkMode },
    },
  } = useContext(AppContext);

  // Calculate distribution buckets and stats
  const { buckets, winRate, avgWin, avgLoss } = useMemo(() => {
    if (!soldHoldings || soldHoldings.length === 0) {
      return {
        buckets: [],
        winRate: 0,
        avgWin: 0,
        avgLoss: 0,
      };
    }

    // Calculate P&L percentage for each holding
    const plPercentages = soldHoldings.map((holding) => {
      const totalCost = holding.totalCost || 0;
      const totalValue = holding.totalValue || 0;
      if (totalCost === 0) return 0;
      return ((totalValue - totalCost) / totalCost) * 100;
    });

    // Define buckets
    const bucketRanges = [
      { min: -Infinity, max: -50, label: '<-50%', isProfit: false },
      { min: -50, max: -20, label: '-50%–-20%', isProfit: false },
      { min: -20, max: 0, label: '-20%–0%', isProfit: false },
      { min: 0, max: 20, label: '0%–20%', isProfit: true },
      { min: 20, max: 50, label: '20%–50%', isProfit: true },
      { min: 50, max: 100, label: '50%–100%', isProfit: true },
      { min: 100, max: 200, label: '100%–200%', isProfit: true },
      { min: 200, max: Infinity, label: '>200%', isProfit: true },
    ];

    const distributionBuckets: DistributionBucket[] = bucketRanges.map((range) => ({
      range: range.label,
      count: plPercentages.filter((pl) => pl > range.min && pl <= range.max).length,
      isProfit: range.isProfit,
    }));

    // Calculate stats
    const wins = plPercentages.filter((pl) => pl > 0);
    const losses = plPercentages.filter((pl) => pl < 0);

    const calculatedWinRate = plPercentages.length > 0 ? (wins.length / plPercentages.length) * 100 : 0;

    const calculatedAvgWin = wins.length > 0 ? wins.reduce((sum, pl) => sum + pl, 0) / wins.length : 0;

    const calculatedAvgLoss = losses.length > 0 ? losses.reduce((sum, pl) => sum + pl, 0) / losses.length : 0;

    return {
      buckets: distributionBuckets,
      winRate: calculatedWinRate,
      avgWin: calculatedAvgWin,
      avgLoss: calculatedAvgLoss,
    };
  }, [soldHoldings]);

  const chartOptions: Highcharts.Options = useMemo(
    () => ({
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 200,
        spacing: [10, 10, 10, 10],
      },
      title: {
        text: undefined,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories: buckets.map((b) => b.range),
        labels: {
          style: {
            color: isDarkMode ? '#94A3B8' : '#6B7280',
            fontSize: '10px',
          },
          rotation: -45,
        },
        lineColor: isDarkMode ? '#334155' : '#E5E7EB',
        tickColor: 'transparent',
      },
      yAxis: {
        title: {
          text: undefined,
        },
        labels: {
          style: {
            color: isDarkMode ? '#94A3B8' : '#6B7280',
            fontSize: '10px',
          },
        },
        gridLineColor: isDarkMode ? '#334155' : '#E5E7EB',
        gridLineDashStyle: 'Dash',
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#1E293B' : '#FFFFFF',
        borderColor: isDarkMode ? '#334155' : '#E5E7EB',
        borderRadius: 8,
        style: {
          color: isDarkMode ? '#F8FAFC' : '#111827',
        },
        formatter() {
          const point = this as Highcharts.TooltipFormatterContextObject;
          return `<b>${point.x}</b><br/>Count: ${point.y}`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: 4,
          borderWidth: 0,
          pointPadding: 0.1,
          groupPadding: 0.1,
        },
      },
      series: [
        {
          type: 'column',
          name: 'Trades',
          data: buckets.map((b) => {
            const profitColor = isDarkMode ? '#4ADE80' : '#22C55E';
            const lossColor = isDarkMode ? '#F87171' : '#EF4444';
            return {
              y: b.count,
              color: b.isProfit ? profitColor : lossColor,
            };
          }),
        },
      ],
    }),
    [buckets, isDarkMode],
  );

  if (isLoading) {
    return (
      <div className="bg-slate-800 dark:bg-slate-800 rounded-2xl p-5 mx-4 mb-4 animate-pulse">
        <div className="h-6 w-40 bg-slate-700 rounded mb-4" />
        <div className="h-48 bg-slate-700 rounded" />
      </div>
    );
  }

  if (!soldHoldings || soldHoldings.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 mx-4 mb-4 border border-gray-100 dark:border-slate-700">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Trade Distribution</h3>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Chart */}
        <div className="flex-1 min-w-0">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>

        {/* Stats Cards */}
        <div className="flex flex-row lg:flex-col gap-2 lg:w-32">
          {/* Win Rate */}
          <div className="flex-1 lg:flex-none bg-slate-100 dark:bg-slate-700/80 rounded-lg px-3 py-2">
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">
              Win Rate
            </p>
            <p className="text-base font-semibold text-teal-600 dark:text-teal-400">{winRate.toFixed(0)}%</p>
          </div>

          {/* Avg Win */}
          <div className="flex-1 lg:flex-none bg-emerald-50 dark:bg-emerald-950/50 rounded-lg px-3 py-2">
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">
              Avg Win
            </p>
            <p className="text-base font-semibold text-green-600 dark:text-green-400">+{avgWin.toFixed(1)}%</p>
          </div>

          {/* Avg Loss */}
          <div className="flex-1 lg:flex-none bg-red-50 dark:bg-red-950/50 rounded-lg px-3 py-2">
            <p className="text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wide font-medium">
              Avg Loss
            </p>
            <p className="text-base font-semibold text-red-600 dark:text-red-400">{avgLoss.toFixed(1)}%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeDistribution;
